import { Account, TournamentCompetition, TournamentEntry, type TournamentMatch } from "@mixitone/models";
import { List, setupModelRelations } from "@mixitone/oom";
import { withStateToggle } from "@mixitone/util";
import { captureException } from "@mixitone/telemetry";
import MixitoneController from "controllers/MixitoneController";
import TournamentController from "components/Pages/Tournament/TournamentController";
import {
  groupStageMatchesComplete,
  playoffMatchesStarted,
  roundRobinPlayoffSettings,
} from "components/Pages/Tournament/tournamentHelpers";
import { type FormatName, getFormat } from "tournament-formats";
import { TournamentDataAdapter } from "tournament-formats/TournamentDataAdapter";
import type { StandingsData } from "tournament-formats/TournamentFormat";

interface State {
  loading: boolean;
  competition: TournamentCompetition | null;
  entries: List<TournamentEntry>;
  matches: List<TournamentMatch>;
  standingsData: StandingsData | null;
  error: string | null;
}

interface Props {
  competitionId: string;
}

/**
 * Controller for tournament standings
 * This controller is responsible for loading a competition and its entries,
 * then calculating standings using the format's getStandings method
 */
class TournamentStandingsController extends MixitoneController<State, Props> {
  get initialState(): State {
    return {
      loading: false,
      competition: null,
      entries: new List(),
      matches: new List(),
      standingsData: null,
      error: null,
    };
  }

  override async initialize(props: Props) {
    await this.changeProps(props);

    try {
      this.observe(async () => {
        const tournamentController = this.get(TournamentController);
        const competitionId = this.state.competition?.id;
        if (!competitionId) return;
        this.state.entries = new List(
          ...tournamentController.state.entries.filter(
            (entry) => entry.tournament_competition_id === competitionId,
          ),
        );
        this.state.matches = new List(
          ...tournamentController.state.matches.filter(
            (match) => match.tournament_competition_id === competitionId,
          ),
        );
        await this.calculateStandings();
      });
    } catch {
      // Standings can render outside TournamentController (tests / isolated drawers).
    }
  }

  override async changeProps(newProps: Props) {
    if (newProps.competitionId !== this.state.competition?.id) {
      await this.loadCompetition(newProps.competitionId);

      const subscription = await this.state.competition!.subscribe(async (t) => {
        this.state.competition = t;
        await this.loadRelations();
        await this.calculateStandings();
      });

      this.addDependency("competition", subscription);
    }
  }

  @withStateToggle("loading")
  async loadCompetition(competitionId: string) {
    try {
      this.state.error = null;

      // Load competition
      const competition = await TournamentCompetition.query()
        .eq("account_id", Account.current!.id)
        .find(competitionId);

      if (!competition) {
        this.state.error = "Competition not found";
        return;
      }

      this.state.competition = competition;
      await this.loadRelations();
      await this.calculateStandings();
    } catch (error) {
      console.error("Error loading competition:", error);
      this.state.error = error instanceof Error ? error.message : "Failed to load competition";
      captureException(error, {
        tags: {
          competitionId,
        },
      });
    }
  }

  async loadRelations() {
    try {
      const adapter = new TournamentDataAdapter();
      const [entries, matches] = await Promise.all([
        TournamentEntry.query().eq("tournament_competition_id", this.state.competition!.id).all(),
        adapter.loadProjectedMatches(this.state.competition!),
      ]);

      this.state.entries = new List(...entries);
      this.state.matches = new List(...matches);

      // Setup model relations
      setupModelRelations({
        competition: this.state.competition!,
        entries,
        matches,
      });
    } catch (error) {
      console.error("Error loading competition:", error);
      this.state.error = error instanceof Error ? error.message : "Failed to load competition";
      captureException(error, {
        tags: {
          competitionId: this.state.competition?.id,
        },
      });
    }
  }

  async calculateStandings() {
    try {
      const { competition } = this.state;
      if (!competition) {
        this.state.error = "Competition not found";
        return;
      }

      const adapter = new TournamentDataAdapter();
      const state = await adapter.loadState(competition);

      const format = getFormat(competition.format as FormatName);
      const standingsData = format.getStandings(state, competition.format_settings);

      this.state.standingsData = standingsData;
    } catch (error) {
      console.error("Error calculating standings:", error);
      this.state.error = error instanceof Error ? error.message : "Failed to calculate standings";
      captureException(error, {
        tags: {
          competitionId: this.state.competition?.id,
        },
      });
    }
  }

  /**
   * Get the name for an entry
   * @param entryId Entry ID
   * @returns Entry name or placeholder
   */
  getEntryName(entryId: string): string {
    const entry = this.state.entries.findById(entryId);
    return entry?.name || `Entry #${entry?.seed || "?"}`;
  }

  get playoffSettings() {
    return roundRobinPlayoffSettings(this.state.competition);
  }

  get groupStageComplete() {
    return groupStageMatchesComplete(this.state.matches);
  }

  get playoffsHaveStarted() {
    return playoffMatchesStarted(this.state.matches);
  }

  get showDivisionColumn() {
    const { enablePlayoffs, divisions } = this.playoffSettings;
    if (!enablePlayoffs || divisions <= 1) return false;
    return this.groupStageComplete || this.state.entries.some((entry) => entry.division !== undefined);
  }

  get canEditPlayoffDivisions() {
    return this.showDivisionColumn && this.groupStageComplete && !this.playoffsHaveStarted;
  }

  async actionSetEntryDivision(entryId: string, division: number | undefined) {
    if (!this.canEditPlayoffDivisions) return;
    const standingsEntry = this.state.entries.findById(entryId);
    const competition = this.state.competition;
    if (!standingsEntry || !competition?.id) return;
    if (standingsEntry.division === division) return;

    try {
      const tournamentController = this.get(TournamentController);
      await tournamentController.actionSetPlayoffDivision(competition.id, entryId, division);
    } catch {
      window.dispatchEvent(
        new CustomEvent("setPlayoffDivision", {
          detail: { competitionId: competition.id, entryId, division },
        }),
      );
    }
    await this.loadRelations();
    await this.calculateStandings();
  }

  async actionRegenerateGroups() {
    const competition = this.state.competition;

    if (!competition) {
      return;
    }

    // Get TournamentController and call actionRegenerateGroups
    const tournamentController = this.get(TournamentController);
    await tournamentController.actionRegenerateGroups(competition.id!);
  }

  async actionSetGroupsLocked(locked: boolean) {
    const competition = this.state.competition;
    if (!competition?.id) return;

    try {
      const tournamentController = this.get(TournamentController);
      await tournamentController.actionSetGroupsLocked(competition.id, locked);
    } catch {
      window.dispatchEvent(
        new CustomEvent("setGroupsLocked", {
          detail: { competitionId: competition.id, locked },
        }),
      );
    }
  }

  async actionResetPlayoffDivisions() {
    const competition = this.state.competition;
    if (!competition?.id) return;
    const tournamentController = this.get(TournamentController);
    await tournamentController.actionResetPlayoffDivisions(competition.id);
    await this.loadRelations();
    await this.calculateStandings();
  }
}

export default TournamentStandingsController;
